import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { authenticatedFetch } from '../utils/authenticatedFetch';
import tokenStore from '../utils/tokenStore';
import { API_URL } from '../constants/api';

const IssuesContext = createContext();

export const useIssues = () => {
  const context = useContext(IssuesContext);
  if (!context) {
    throw new Error('useIssues must be used within IssuesProvider');
  }
  return context;
};

export const IssuesProvider = ({ children }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchIssues = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const token = tokenStore.get();
      if (!token) return;

      let url = `${API_URL}/api/issues`;
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (!value || key === 'search') return;
        // 'All' is a UI-only option and should not be sent to the API as a literal assignee.
        if (key === 'assignee' && value === 'All') return;
        params.append(key, value);
      });
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await authenticatedFetch(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const issuesData = await response.json();
        setIssues(issuesData);
      } else {
        console.error('Failed to fetch issues:', response.statusText);
        setError('Failed to fetch issues');
      }
    } catch (error) {
      console.error('Error fetching issues:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createIssue = useCallback(async (issueData) => {
    try {
      const token = tokenStore.get();
      if (!token) throw new Error('No authentication token');

      const response = await authenticatedFetch(`${API_URL}/api/issues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(issueData)
      });

      if (response.ok) {
        const newIssue = await response.json();
        setIssues(prev => [...prev, newIssue]);
        return newIssue;
      } else {
        throw new Error('Failed to create issue');
      }
    } catch (error) {
      console.error('Error creating issue:', error);
      throw error;
    }
  }, []);

  const updateIssue = useCallback(async (issueId, updates) => {
    try {
      const token = tokenStore.get();
      if (!token) throw new Error('No authentication token');

      const response = await authenticatedFetch(`${API_URL}/api/issues/${issueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedIssue = await response.json();
        setIssues(prev => prev.map(issue =>
          // Preserve local parent_id — linkIssue sets it directly on the backend without going
          // through this context, so the backend response would incorrectly overwrite local state.
          issue.id === issueId ? { ...issue, ...updatedIssue, parent_id: issue.parent_id } : issue
        ));
        return updatedIssue;
      } else {
        throw new Error('Failed to update issue');
      }
    } catch (error) {
      console.error('Error updating issue:', error);
      throw error;
    }
  }, []);

  const deleteIssue = useCallback(async (issueId) => {
    try {
      const token = tokenStore.get();
      if (!token) throw new Error('No authentication token');

      const response = await authenticatedFetch(`${API_URL}/api/issues/${issueId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setIssues(prev => prev.filter(issue => issue.id !== issueId && issue.parent_id !== issueId));
        return true;
      } else {
        throw new Error('Failed to delete issue');
      }
    } catch (error) {
      console.error('Error deleting issue:', error);
      throw error;
    }
  }, []);

  const value = useMemo(() => ({
    issues,
    setIssues,
    loading,
    error,
    fetchIssues,
    createIssue,
    updateIssue,
    deleteIssue
  }), [issues, loading, error, fetchIssues, createIssue, updateIssue, deleteIssue]);

  return (
    <IssuesContext.Provider value={value}>
      {children}
    </IssuesContext.Provider>
  );
};

